// === Servicevergoeding post-merge helper ===
//
// BFF treats the service-fee field as readOnly/computed and returns 0 in
// calculation responses regardless of what we send. To still bill the SAA
// Servicevergoeding (config-driven via `prolongationFee.amount`), this helper
// applies the fee once at the PACKAGE level after the package endpoint response:
// it writes the fee into PK_TINV (toegevoegde investering) and adds the
// assurantiebelasting (21%) on top of all per-term/total premium fields, so the
// displayed totals and downstream quote/request endpoints reflect the surcharge.
// Applying at package level means the fee is charged once for the entire
// package regardless of how many products are selected.
//
// Toggle: when `prolongationFee.enabled === false` in config, the per-screen
// fee derivation produces 0, this helper short-circuits, and nothing is
// added — flipping the fee back off requires no code change.

const TAX_RATE = 0.21; // Dutch assurantiebelasting

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Mutates `target` in-place: writes the fee to `feeField` and adds
 * (fee + fee*21%) to all per-term/total premium fields for the given prefix
 * ('PP' or 'PK'). No-op when `fee <= 0` (toggle-off).
 */
const applyServiceFeeSurcharge = (target: any, prefix: 'PP' | 'PK', fee: number, feeField: string): void => {
  if (!target || !fee || fee <= 0) return;

  const tax = round2(fee * TAX_RATE);
  const grossAddition = round2(fee + tax);

  target[feeField] = fee;

  // Net term premium (without tax)
  const ntpKey = `${prefix}_NTP`;
  const ntpvpkKey = `${prefix}_NTPVPK`;
  if (target[ntpKey] !== undefined) target[ntpKey] = round2(Number(target[ntpKey] || 0) + fee);
  if (target[ntpvpkKey] !== undefined) target[ntpvpkKey] = round2(Number(target[ntpvpkKey] || 0) + fee);

  // Tax per term
  const tassKey = `${prefix}_TASS`;
  target[tassKey] = round2(Number(target[tassKey] || 0) + tax);

  // Gross per term + bruto term premium + grand total
  const tppKey = `${prefix}_TPP`;
  const btpKey = `${prefix}_BTP`;
  const ttotKey = `${prefix}_TTOT`;
  if (target[tppKey] !== undefined) target[tppKey] = round2(Number(target[tppKey] || 0) + grossAddition);
  if (target[btpKey] !== undefined) target[btpKey] = round2(Number(target[btpKey] || 0) + grossAddition);
  if (target[ttotKey] !== undefined) target[ttotKey] = round2(Number(target[ttotKey] || 0) + grossAddition);
};

/**
 * Apply Servicevergoeding surcharge at the PACKAGE level (PK_TINV). Operates on
 * Pakket[0].Mantel.PK. This is the canonical entry point — call this once after
 * the package endpoint response is merged so the surcharge is added to the
 * package totals exactly once, regardless of how many products the package
 * contains.
 */
export const applyPkTinv = (pk: any, fee: number): void => {
  applyServiceFeeSurcharge(pk, 'PK', fee, 'PK_TINV');
};

/**
 * Legacy: apply fee surcharge at the PRODUCT level (PP_EKST). Kept for
 * compatibility but no longer called from the workflow — use `applyPkTinv` at
 * package level.
 */
export const applyPpEkstToPP = (pp: any, fee: number): void => {
  applyServiceFeeSurcharge(pp, 'PP', fee, 'PP_EKST');
};
